import React, { FC, ReactElement, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  PanelBlock,
  LayoutContainer,
  BottomLayout,
  AdditionalPanel,
  TopPanelBlock,
  LeftContainer,
  NirvanaButton,
  PanelContent,
} from "./styled";

import { LEFT_PANEL_MIN_WIDTH } from "./constants";

import { PanelWidthDrag } from "./PanelWidthDrag";
import { Panel } from "./Panel";
import { Main } from "./MainContainer";

import { Presentation } from "../../modules/Dashboard/components/Presentation";
import { getLocationType } from "../../ducks/location/selectors";
import { getThemeName } from "../../ducks/theme/selectors";
import { getPanelWidth } from "../../ducks/layout/selectors";
import { useModules } from "../../hooks/appConfig/useModules";
import { useNirvanaMode } from "../../hooks/layout/useNirvanaMode";
import { usePresentationMode } from "../../hooks/layout/usePresentationMode";
import { useIsShared } from "../../hooks/api/useIsShared";
import { useConfigPages } from "../../modules/Dashboard/hooks/useConfigPages";
import { WidgetType } from "../../modules/FeatureCard/types";
import { LayoutProps } from "./types";

export const Layout: FC<LayoutProps> = ({
  main,
  panel,
  presentation,
  portal,
  bottom,
  additionalControls,
  mainControls,
  topControls,
  bottomState,
  bottomHeight,
}) => {
  const { t } = useTranslation("common");
  const themeName = useSelector(getThemeName);
  const panelWidth = useSelector(getPanelWidth);
  const locationType = useSelector(getLocationType);
  const { currentPage } = useConfigPages(WidgetType.Dashboard);
  const modules = useModules();
  const { isNirvanaMode, toggleNirvanaMode } = useNirvanaMode();
  const { isPresentationMode, isProjectConfig } = usePresentationMode();
  const isShared = useIsShared();
  const [isResizing, setIsResizing] = useState(false);

  const panelSettings = useMemo(
    () => modules.panel?.find(({ route }) => route === locationType),
    [locationType, modules.panel],
  );

  const isResizable = !!panelSettings?.layoutOptions?.resizable;
  const isLoading = !currentPage;
  const isProjectPresentation = isProjectConfig && !!(presentation as ReactElement).props.modules.length;
  const isPresentation = isPresentationMode || isProjectPresentation;
  const leftWidth = isProjectConfig ? LEFT_PANEL_MIN_WIDTH : 0;

  return (
    <LayoutContainer>
      {bottomState !== "fullscreen" && (!isShared || !isLoading) && (
        <LeftContainer $isHidden={isNirvanaMode || (isPresentationMode && !isProjectConfig)}>
          <Panel>
            {isResizable && <PanelWidthDrag setIsResizing={setIsResizing} />}
            <PanelContent isResizing={isResizing}>{panel}</PanelContent>
          </Panel>
          {!isPresentationMode && !isResizable && (
            <NirvanaButton
              title={t("nirvanaMode")}
              $isNirvanaMode={isNirvanaMode}
              $bottomHeight={bottomState === "opened" ? bottomHeight : 0}
              onClick={toggleNirvanaMode}
            />
          )}
        </LeftContainer>
      )}
      {isPresentation && <Presentation left={isProjectConfig && panelWidth} />}
      <Main bottomSlot={bottom} bottomState={bottomState} bottomHeight={bottomHeight}>
        {main}
        <TopPanelBlock leftWidth={leftWidth} isPresentationMode={isPresentation}>
          {topControls}
        </TopPanelBlock>
        <BottomLayout
          leftWidth={leftWidth}
          bottomOpened={bottomState === "opened"}
          bottomHeight={bottomHeight}
          themeName={themeName}
          isPresentationMode={isPresentation}
        >
          <PanelBlock>{mainControls}</PanelBlock>
          <AdditionalPanel>{additionalControls}</AdditionalPanel>
        </BottomLayout>
      </Main>
      <div>{portal}</div>
    </LayoutContainer>
  );
};
